import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft,
  ShoppingCart,
  Trash2,
  FileText,
  Package,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2,
  Download,
  Calendar,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Sales() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // invoice number to confirm
  const [searchQuery, setSearchQuery] = useState('');
  const [filterInvoice, setFilterInvoice] = useState(searchParams.get('invoice') || '');
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  
  // Filter states
  const [customerFilter, setCustomerFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, 7, 30, 90

  // Check if user has edit/delete permission
  const canEdit = user?.effective_permissions?.includes('edit_sales') || false;

  const fetchSales = useCallback(async () => {
    if (!companyId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/sales?company_id=${companyId}&limit=500`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSales(data.sales || []);
      }
    } catch (error) {
      console.error('Failed to fetch sales:', error);
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchSales();
    }
  }, [authLoading, user, companyId, fetchSales]);

  const handleDelete = async (salesId) => {
    setDeletingId(salesId);
    try {
      const response = await fetch(`${API_URL}/api/sales/${salesId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Entry deleted');
        fetchSales();
      } else {
        toast.error('Failed to delete entry');
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteInvoice = async (invoiceNumber) => {
    setDeletingInvoice(invoiceNumber);
    try {
      // Different endpoint for "No Invoice" vs regular invoices
      // Use query params instead of path params to handle slashes in invoice numbers
      const url = invoiceNumber === 'No Invoice'
        ? `${API_URL}/api/sales/no-invoice?company_id=${companyId}`
        : `${API_URL}/api/sales/by-invoice?invoice_number=${encodeURIComponent(invoiceNumber)}&company_id=${companyId}`;
      
      console.log('Deleting invoice:', invoiceNumber, 'URL:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include'
      });

      console.log('Delete response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchSales();
        setConfirmDelete(null);
      } else {
        const errorText = await response.text();
        console.error('Delete error:', errorText);
        try {
          const error = JSON.parse(errorText);
          toast.error(error.detail || 'Failed to delete');
        } catch {
          toast.error('Failed to delete: ' + errorText);
        }
      }
    } catch (error) {
      console.error('Delete exception:', error);
      toast.error('Failed to delete: ' + error.message);
    } finally {
      setDeletingInvoice(null);
    }
  };

  const handleDownloadInvoice = async (invoiceNumber) => {
    try {
      const url = invoiceNumber 
        ? `${API_URL}/api/sales/export?company_id=${companyId}&invoice_number=${encodeURIComponent(invoiceNumber)}`
        : `${API_URL}/api/sales/export?company_id=${companyId}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `sales_${invoiceNumber || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Download started');
      } else {
        toast.error('Failed to download');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get unique customers for filter dropdown
  const uniqueCustomers = [...new Set(sales.map(s => s.customer_name).filter(Boolean))].sort();

  // Filter sales
  const filteredSales = sales.filter(sale => {
    // Search filter
    const matchesSearch = !searchQuery || 
      sale.item_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sale.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sale.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Invoice filter
    const matchesInvoice = !filterInvoice || sale.invoice_number === filterInvoice;
    
    // Customer filter
    if (customerFilter !== 'all' && sale.customer_name !== customerFilter) {
      return false;
    }
    
    // Date filter
    if (dateFilter !== 'all' && sale.invoice_date) {
      const days = parseInt(dateFilter);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      const itemDate = new Date(sale.invoice_date);
      if (itemDate < cutoffDate) return false;
    }
    
    return matchesSearch && matchesInvoice;
  });

  // Group sales by invoice number
  const groupedSales = filteredSales.reduce((acc, sale) => {
    const key = sale.invoice_number || 'No Invoice';
    if (!acc[key]) {
      acc[key] = {
        invoice_number: sale.invoice_number,
        customer_name: sale.customer_name,
        invoice_date: sale.invoice_date,
        items: []
      };
    }
    // Update the group's invoice_date to the most recent
    if (sale.invoice_date && (!acc[key].invoice_date || sale.invoice_date > acc[key].invoice_date)) {
      acc[key].invoice_date = sale.invoice_date;
    }
    acc[key].items.push(sale);
    return acc;
  }, {});

  // Sort grouped sales by date (most recent first)
  const sortedGroupedSales = Object.entries(groupedSales).sort(([, a], [, b]) => {
    const dateA = a.invoice_date ? new Date(a.invoice_date) : new Date(0);
    const dateB = b.invoice_date ? new Date(b.invoice_date) : new Date(0);
    return dateB - dateA; // Most recent first
  });

  const toggleInvoice = (invoiceNum) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceNum)) {
      newExpanded.delete(invoiceNum);
    } else {
      newExpanded.add(invoiceNum);
    }
    setExpandedInvoices(newExpanded);
  };

  const uniqueInvoices = [...new Set(sales.map(s => s.invoice_number).filter(Boolean))];

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="sales-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="h-10 w-10 p-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Sales</h1>
              <p className="text-slate-500 mt-1">View sales entries - {selectedCompany?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleDownloadInvoice(null)}
              data-testid="download-all-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
            <Button
              onClick={() => navigate('/invoices')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="upload-invoice-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Upload Invoice
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{sales.length}</p>
                <p className="text-sm text-slate-500">Total Items</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{uniqueInvoices.length}</p>
                <p className="text-sm text-slate-500">Invoices</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {[...new Set(sales.map(s => s.customer_name).filter(Boolean))].length}
                </p>
                <p className="text-sm text-slate-500">Customers</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Filter className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{filteredSales.length}</p>
                <p className="text-sm text-slate-500">Filtered</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col gap-3">
            {/* Top row: Search and Invoice filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items, customers, or invoice numbers..."
                  className="pl-10 h-11"
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={filterInvoice}
                  onChange={(e) => setFilterInvoice(e.target.value)}
                  className="h-11 px-3 border border-slate-200 rounded-lg text-sm bg-white min-w-[180px]"
                  data-testid="invoice-filter"
                >
                  <option value="">All Invoices</option>
                  {uniqueInvoices.map(inv => (
                    <option key={inv} value={inv}>{inv}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Bottom row: Additional Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                <Select value={customerFilter} onValueChange={setCustomerFilter}>
                  <SelectTrigger className="w-48 h-9" data-testid="customer-filter">
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {uniqueCustomers.map(customer => (
                      <SelectItem key={customer} value={customer}>{customer}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-36 h-9" data-testid="date-filter">
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="7">Last 7 Days</SelectItem>
                    <SelectItem value="30">Last 30 Days</SelectItem>
                    <SelectItem value="90">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(searchQuery || filterInvoice || customerFilter !== 'all' || dateFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSearchQuery(''); setFilterInvoice(''); setCustomerFilter('all'); setDateFilter('all'); }}
                  className="h-9 text-slate-500"
                >
                  Clear All Filters
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Sales List - Grouped by Invoice (sorted by date, most recent first) */}
        <div className="space-y-4" data-testid="sales-list">
          {sortedGroupedSales.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg text-center py-12">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">
                {sales.length === 0 ? 'No sales recorded yet' : 'No sales match your filters'}
              </p>
              <Button
                onClick={() => navigate('/invoices')}
                variant="outline"
              >
                <FileText className="w-4 h-4 mr-2" />
                Upload Sales Invoice
              </Button>
            </div>
          ) : (
            sortedGroupedSales.map(([invoiceNum, group]) => (
              <div key={invoiceNum} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {/* Invoice Header */}
                <div 
                  className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => toggleInvoice(invoiceNum)}
                  data-testid={`invoice-group-${invoiceNum}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Invoice #{invoiceNum}</p>
                      <p className="text-sm text-slate-500">
                        {group.customer_name} • {formatDate(group.invoice_date)} • {group.items.length} items
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-600 hidden sm:block">
                      {group.items.reduce((sum, item) => sum + (item.quantity || 0), 0).toFixed(1)} total qty
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(invoiceNum); }}
                      className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                      title="Download Invoice"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(invoiceNum); }}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-invoice-${invoiceNum}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {expandedInvoices.has(invoiceNum) ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Delete Confirmation */}
                {confirmDelete === invoiceNum && (
                  <div className="border-t border-red-200 bg-red-50 p-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">
                          {invoiceNum === 'No Invoice' ? 'Delete all items without invoice?' : 'Delete entire invoice?'}
                        </p>
                        <p className="text-sm text-red-600">
                          This will delete all {group.items.length} items {invoiceNum !== 'No Invoice' && `from invoice ${invoiceNum}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteInvoice(invoiceNum); }}
                          disabled={deletingInvoice === invoiceNum}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {deletingInvoice === invoiceNum ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Delete All'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Invoice Items */}
                {expandedInvoices.has(invoiceNum) && (
                  <div className="border-t border-slate-200">
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs font-medium text-slate-500 uppercase bg-slate-50">
                            <th className="px-4 py-3">Item</th>
                            <th className="px-4 py-3">Quantity</th>
                            <th className="px-4 py-3">Added By</th>
                            {canEdit && <th className="px-4 py-3 w-20">Actions</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {group.items.map((sale) => (
                            <tr key={sale.sales_id} className="hover:bg-slate-50" data-testid={`sale-row-${sale.sales_id}`}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium text-slate-900">{sale.item_name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-slate-900">{sale.quantity}</span>
                                <span className="text-slate-500 ml-1">{sale.unit}</span>
                              </td>
                              <td className="px-4 py-3 text-slate-600 text-sm">{sale.created_by_name}</td>
                              {canEdit && (
                                <td className="px-4 py-3">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(sale.sales_id); }}
                                    disabled={deletingId === sale.sales_id}
                                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden p-3 space-y-2">
                      {group.items.map((sale) => (
                        <div
                          key={sale.sales_id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                          data-testid={`sale-card-${sale.sales_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Package className="w-5 h-5 text-blue-500" />
                            <div>
                              <p className="font-medium text-slate-900">{sale.item_name}</p>
                              <p className="text-sm text-slate-500">{sale.quantity} {sale.unit}</p>
                            </div>
                          </div>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(sale.sales_id)}
                              disabled={deletingId === sale.sales_id}
                              className="h-8 w-8 p-0 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
